import Cookies from "js-cookie"
import { Base64 } from "js-base64"
import {
  AUTH_MAGIC_LINK_FAILED,
  AUTH_MAGIC_LINK_USED,
} from "@kiwicom/frontend-common/modules/MagicLogin/consts/events"

import jsonFetch from "common/services/fetch/jsonFetch"
import cuckoo from "common/services/cuckoo/cuckoo"
import mapperUser from "common/services/user/mapperUser"
import { cuckooEvents } from "common/consts/enums"
import authStore from "common/services/user/authStore"
import { brandingIdSelector } from "common/services/branding/selectors"
import apiBookingTokens from "mmb/services/apiBookingTokens/apiBookingTokens"
import { EXPIRY_DAYS, UA_SESSION_TOKEN, KW_AUTH_TOKEN } from "common/consts/auth"

// noinspection SpellCheckingInspection
const USER = "5433ecccaff67"

// Private methods
function _transport(endpoint, body) {
  // eslint-disable-line no-underscore-dangle
  const { options } = window.reduxStore.getState()

  const Authorization = `Basic ${Base64.encode(`${USER}:${authStore.token}`)}`

  return jsonFetch(`${options.get("authApiUrl")}/v1/${endpoint}`, {
    headers: {
      Authorization,
    },
    body,
  }).then(res => {
    // Errors should be returned with HTTP status code other then 200!
    if (!res.error_code) return res
    return Promise.reject(new Error(res.message))
  })
}

const removeLoginCookie = () => {
  // remove client side cookie
  Cookies.remove(UA_SESSION_TOKEN, Skypicker.cookieOptions)
  // remove server side cookie
  Cookies.remove(UA_SESSION_TOKEN)
  // remove KwAuthToken
  Cookies.remove(KW_AUTH_TOKEN)
}

function processKWTokenFailed(error) {
  cuckoo.error(AUTH_MAGIC_LINK_FAILED, { error: String(error) })
  authStore.kwAuthToken = null
  removeLoginCookie()
  throw error
}

function logoutCallback(bid) {
  return () => {
    cuckoo.infinario("userLogout")
    $("body").trigger("user:logout")
    apiBookingTokens.remove(bid)
    Cookies.remove(`simple_token-${bid}`)
    removeLoginCookie()
  }
}

function loadUserInfo(token) {
  const state = window.reduxStore.getState()
  const { options } = state
  const { currencies } = state

  // "bookingApiUrl" is not a mistake
  const bookingSignUrl = `${options.get("bookingApiUrl")}/api/v0.1/users/self?token=${token}`
  const bookingPromise = jsonFetch(bookingSignUrl)
  const signPromise = _transport("user.get", { user: "self" })

  return Promise.all([signPromise, bookingPromise])
    .then(([user, booking]) => mapperUser({ ...user[0], ...booking, token }, currencies))
    .catch(() => {
      cuckoo.track(cuckooEvents.AUTH_LOGIN_ERROR_RETRIEVING_USER)
      throw new Error("Skypicker API error when retrieving user")
    })
}

// store user and trigger global event
const userLogged = (user, loginWith) => {
  cuckoo.infinario("userLogin", { loginWith })
  cuckoo.infinario("identify", user.email)
  $("body").trigger("user:login", user)
  cuckoo.gaIdentify(user.id)
}

export function login(data) {
  cuckoo.track(cuckooEvents.AUTH_LOGIN_WITH_PASSWORD_REQUEST, {
    email: data.get("email"),
    passwordLength: data.get("password").length,
  })

  return _transport("user.login", {
    login: data.get("email"),
    password: data.get("password"),
    brand: brandingIdSelector(window.reduxStore.getState()),
  }).then(res => {
    if (res.locks && res.locks.length > 0) {
      throw new Error("User is locked")
    } else {
      authStore.token = res.token
      Cookies.set(UA_SESSION_TOKEN, res.token, {
        ...Skypicker.cookieOptions,
        expires: EXPIRY_DAYS, // https://github.com/js-cookie/js-cookie#basic-usage
      })
      return loadUserInfo(res.token).then(user => {
        userLogged(user, "password")
        return user
      })
    }
  })
}

export function processKWToken(state, kwAuthToken) {
  const { options, currencies } = state
  const bookingSignUrl = `${options.get("bookingApiUrl")}/api/v0.1/users/self`
  authStore.kwAuthToken = kwAuthToken
  const headers = { "KW-Auth-Token": kwAuthToken }

  return jsonFetch(bookingSignUrl, { headers })
    .then(bookingIdentity => mapperUser({ ...bookingIdentity }, currencies))
    .then(user => {
      cuckoo.track(AUTH_MAGIC_LINK_USED, { email: user.email })

      return user
    })
    .catch(processKWTokenFailed)
}

export function loginWithCookie() {
  authStore.token = Cookies.get(UA_SESSION_TOKEN)

  return loadUserInfo(authStore.token)
    .then(user => {
      userLogged(user, "cookieToken")
      return user
    })
    .catch(err => {
      authStore.token = null
      removeLoginCookie()
      throw err
    })
}

/**
 * Logout user, trigger global event and remove cookie with token
 */
export function logout(bid) {
  cuckoo.track(cuckooEvents.AUTH_LOGOUT)

  // User is authenticated with KW-Auth-Token
  if (!apiBookingTokens.getAuth()) {
    return Promise.resolve(logoutCallback(bid)())
  }

  return _transport("user.logout", {}).then(logoutCallback(bid))
}

export function signup(data) {
  // TODO: consider adding userdata
  cuckoo.track(cuckooEvents.AUTH_SIGNUP_REQUEST)

  const basicUserData = {
    email: data.get("email"),
    first_name: data.get("firstName"),
    last_name: data.get("lastName"),
  }

  return _transport("user.save", {
    ...basicUserData,
    login: data.get("email"), // Email is used to login
    password: data.get("password"),
    brand: brandingIdSelector(window.reduxStore.getState()),
  }).then(() => cuckoo.infinario("userSignUp", basicUserData))
}

export function verifyEmail(token) {
  return _transport("user.verifyEmail", {
    email_token: token,
  })
}

export function resetPassword(email) {
  return _transport("user.resetPassword", {
    login: email,
    brand: brandingIdSelector(window.reduxStore.getState()),
  })
}

export function changePassword(newPass, currentPass) {
  return _transport("user.changePassword", {
    new_password: newPass,
    current_password: currentPass,
    user_id: "self",
  })
}

export function changePasswordWithToken(token, newPass) {
  return _transport("user.changePassword", {
    new_password: newPass,
    password_token: token,
  })
}

export function changeName(firstName, lastName) {
  return _transport("user.save", {
    user_id: "self",
    first_name: firstName,
    last_name: lastName,
  })
}
